import type { ActivityResponse, Attachment, UpdateActivityPartialRequest } from '@stream-io/feeds-client'

import { ValkTalkPost, ValkTalkPostFormData } from './ValkTalkPost'
import { getFeedTypeFromFids } from './ValkTalkPostHelpers'

export const transformActivityToValkTalkPost = (activity: ActivityResponse): ValkTalkPost | undefined => {
    const feedType = getFeedTypeFromFids(activity.feeds)

    if (! feedType) {
        return undefined
    }

    const imageUrls = activity.attachments
        .filter(attachment => attachment.type === 'image')
        .map(attachment => attachment.image_url ?? attachment.asset_url)
        .filter((url): url is string => Boolean(url))

    return {
        id: activity.id,
        feedType,
        title: activity.custom?.title ?? '',
        text: activity.text ?? '',
        images: imageUrls.map(url => ({ id: url, url })),
        author: {
            id: activity.user.id,
            name: activity.user.name,
        },
        createdAt: new Date(activity.created_at),
    }
}

export const transformImageUrlsToAttachments = (imageUrls: string[]): Attachment[] => imageUrls.map(url => ({
    type: 'image',
    image_url: url,
    custom: {},
}))

// The multipart body of Valkplanner's feed messages endpoint, which picks the feed and post type from `feed_type`
export const transformValkTalkPostFormDataToFeedMessageFormData = (formData: ValkTalkPostFormData): FormData => {
    const body = new FormData()

    body.append('feed_type', formData.feedType)
    body.append('title', formData.title.trim())
    body.append('text', formData.text.trim())

    formData.images.forEach(image => {
        if (image.file) {
            body.append('images[]', image.file)
        }
    })

    return body
}

// A partial update, so custom data and settings that aren't managed by this form are kept
export const transformValkTalkPostFormDataToUpdateActivityPartialRequest = (
    formData: ValkTalkPostFormData,
    imageUrls: string[],
): UpdateActivityPartialRequest => ({
    set: {
        text: formData.text.trim(),
        'custom.title': formData.title.trim(),
        attachments: transformImageUrlsToAttachments(imageUrls),
    },
})
